import {
  getTaskSingleSize,
  getWidgetNoteSize,
  getWidgetOverviewSize,
  getWeatherSize,
  getWidgetIntegrationTurnpikeSize,
} from "widgets";
import { useCallback } from "react";
import { WidgetPos } from "./types";
import { graphql, useMutation } from "react-relay";
import { useRearangeWidgetsMutation } from "./__generated__/useRearangeWidgetsMutation.graphql";

export const useRearangeWidgets = (storecastId: string) => {
  const [mutate] = useMutation<useRearangeWidgetsMutation>(graphql`
    mutation useRearangeWidgetsMutation(
      $storecastId: ID!
      $positions: WidgetPositionInput!
    ) {
      dashboard(dashboardId: $storecastId) {
        setWidgetPositions(positions: $positions) {
          id
          widgets {
            ...useWidgetToPos_widget
          }
        }
      }
    }
  `);

  const setPositions = useCallback(
    (pos: WidgetPos[]) => {
      return mutate({
        optimisticUpdater: (store) => {
          pos.forEach((pos) => {
            const widget = store.get(pos.id);

            if (!widget) return;

            widget.setValue(pos.c * 2 + pos.x, "positionX");
            widget.setValue(pos.y, "positionY");

            switch (pos.kind) {
              case "WidgetSpace":
              case "WidgetFile":
              case "WidgetIssueSummary":
              case "WidgetEmployeeSchedule":
              case "WidgetEmployeeLeave":
              case "WidgetTeamSchedule":
              case "WidgetCalendarOverview":
              case "WidgetIntegrationPushAppy":
              case "WidgetIntegrationIpool":
              case "WidgetIssueOverview":
              case "WidgetRoutineOverview":
              case "WidgetSignalOverview":
              case "WidgetEmployeeRoleOverview":
              case "WidgetTodoOverview":
              case "WidgetIntegrationFlourishPraise":
              case "WidgetKPI":
              case "WidgetList":
              case "WidgetTable":
                widget.setValue(getWidgetOverviewSize(pos), "overviewSize");
                break;
              case "WidgetNewsfeed":
                widget.setValue(getWidgetOverviewSize(pos), "overviewSize");
                break;
              case "WidgetTaskSingle":
                widget.setValue(getTaskSingleSize(pos), "taskSingleSize");
                break;
              case "WidgetWeather":
                widget.setValue(getWeatherSize(pos), "weatherSize");
                break;
              case "WidgetNote":
                widget.setValue(getWidgetNoteSize(pos), "noteSize");
                break;
              case "WidgetIntegrationTurnpike":
                widget.setValue(
                  getWidgetIntegrationTurnpikeSize(pos),
                  "turnpikeSize"
                );
                break;
            }
          });
        },
        variables: {
          storecastId,
          positions: {
            setSpacePositions: pos
              .filter((v) => v.kind === "WidgetSpace")
              .map((pos) => ({
                positionX: pos.c * 2 + pos.x,
                positionY: pos.y,
                widgetId: pos.id,
              })),
            setIntegrationTurnpikePositions: pos
              .filter((v) => v.kind === "WidgetIntegrationTurnpike")
              .map((pos) => ({
                positionX: pos.c * 2 + pos.x,
                positionY: pos.y,
                widgetId: pos.id,
                setSize: getWidgetIntegrationTurnpikeSize(pos),
              })),

            setCalendarWidgets: pos
              .filter((v) => v.kind === "WidgetCalendar")
              .map((pos) => ({
                positionX: pos.c * 2 + pos.x,
                positionY: pos.y,
                widgetId: pos.id,
                setSize: getWidgetOverviewSize(pos),
              })),
            setCalendarOverviewPositions: pos
              .filter((v) => v.kind === "WidgetCalendarOverview")
              .map((pos) => ({
                positionX: pos.c * 2 + pos.x,
                positionY: pos.y,
                widgetId: pos.id,
                setSize: getWidgetOverviewSize(pos),
              })),
            setIssueSummaryPositions: pos
              .filter((v) => v.kind === "WidgetIssueSummary")
              .map((pos) => ({
                positionX: pos.c * 2 + pos.x,
                positionY: pos.y,
                widgetId: pos.id,
                setSize: getWidgetOverviewSize(pos),
              })),
            setTodoSummaryPositions: pos
              .filter((v) => v.kind === "WidgetTodoSummary")
              .map((pos) => ({
                positionX: pos.c * 2 + pos.x,
                positionY: pos.y,
                widgetId: pos.id,
                setSize: getWidgetOverviewSize(pos),
              })),
            setEmployeeSchedulePositions: pos
              .filter((v) => v.kind === "WidgetEmployeeSchedule")
              .map((pos) => ({
                positionX: pos.c * 2 + pos.x,
                positionY: pos.y,
                widgetId: pos.id,
                setSize: getWidgetOverviewSize(pos),
              })),
            setEmployeeLeavePositions: pos
              .filter((v) => v.kind === "WidgetEmployeeLeave")
              .map((pos) => ({
                positionX: pos.c * 2 + pos.x,
                positionY: pos.y,
                widgetId: pos.id,
                setSize: getWidgetOverviewSize(pos),
              })),
            setTeamSchedulePositions: pos
              .filter((v) => v.kind === "WidgetTeamSchedule")
              .map((pos) => ({
                positionX: pos.c * 2 + pos.x,
                positionY: pos.y,
                widgetId: pos.id,
                setSize: getWidgetOverviewSize(pos),
              })),
            setFilePositions: pos
              .filter((v) => v.kind === "WidgetFile")
              .map((pos) => ({
                positionX: pos.c * 2 + pos.x,
                positionY: pos.y,
                widgetId: pos.id,
                setSize: getWidgetOverviewSize(pos),
              })),
            setIntegrationFlourishPraisePositions: pos
              .filter((v) => v.kind === "WidgetIntegrationFlourishPraise")
              .map((pos) => ({
                positionX: pos.c * 2 + pos.x,
                positionY: pos.y,
                widgetId: pos.id,
                setSize: getWidgetOverviewSize(pos),
              })),
            setIntegrationPushAppyPositions: pos
              .filter((v) => v.kind === "WidgetIntegrationPushAppy")
              .map((pos) => ({
                positionX: pos.c * 2 + pos.x,
                positionY: pos.y,
                widgetId: pos.id,
                setSize: getWidgetOverviewSize(pos),
              })),
            setIntegrationIpoolPositions: pos
              .filter((v) => v.kind === "WidgetIntegrationIpool")
              .map((pos) => ({
                positionX: pos.c * 2 + pos.x,
                positionY: pos.y,
                widgetId: pos.id,
                setSize: getWidgetOverviewSize(pos),
              })),
            setIssueOverviewPositions: pos
              .filter((v) => v.kind === "WidgetIssueOverview")
              .map((pos) => ({
                positionX: pos.c * 2 + pos.x,
                positionY: pos.y,
                widgetId: pos.id,
                setSize: getWidgetOverviewSize(pos),
              })),
            setRoutineOverviewPositions: pos
              .filter((v) => v.kind === "WidgetRoutineOverview")
              .map((pos) => ({
                positionX: pos.c * 2 + pos.x,
                positionY: pos.y,
                widgetId: pos.id,
                setSize: getWidgetOverviewSize(pos),
              })),
            setTodoOverviewPositions: pos
              .filter((v) => v.kind === "WidgetTodoOverview")
              .map((pos) => ({
                positionX: pos.c * 2 + pos.x,
                positionY: pos.y,
                widgetId: pos.id,
                setSize: getWidgetOverviewSize(pos),
              })),
            setSignalOverviewPositions: pos
              .filter((v) => v.kind === "WidgetSignalOverview")
              .map((pos) => ({
                positionX: pos.c * 2 + pos.x,
                positionY: pos.y,
                widgetId: pos.id,
                setSize: getWidgetOverviewSize(pos),
              })),
            setEmployeeRoleOverviewPositions: pos
              .filter((v) => v.kind === "WidgetEmployeeRoleOverview")
              .map((pos) => ({
                positionX: pos.c * 2 + pos.x,
                positionY: pos.y,
                widgetId: pos.id,
                setSize: getWidgetOverviewSize(pos),
              })),

            setRoutinePositivePercentagePositions: pos
              .filter((v) => v.kind === "WidgetRoutinePositivePercentage")
              .map((pos) => ({
                positionX: pos.c * 2 + pos.x,
                positionY: pos.y,
                widgetId: pos.id,
              })),
            setTaskCollectionPositions: pos
              .filter((v) => v.kind === "WidgetTaskCollection")
              .map((pos) => ({
                positionX: pos.c * 2 + pos.x,
                positionY: pos.y,
                widgetId: pos.id,
              })),
            setNotePositions: pos
              .filter((v) => v.kind === "WidgetNote")
              .map((pos) => ({
                positionX: pos.c * 2 + pos.x,
                positionY: pos.y,
                widgetId: pos.id,
                setSize: getWidgetNoteSize(pos),
              })),
            setWeatherPositions: pos
              .filter((v) => v.kind === "WidgetWeather")
              .map((pos) => ({
                positionX: pos.c * 2 + pos.x,
                positionY: pos.y,
                widgetId: pos.id,
                setSize: getWeatherSize(pos),
              })),

            setTaskSinglePositions: pos
              .filter((v) => v.kind === "WidgetTaskSingle")
              .map((pos) => ({
                positionX: pos.c * 2 + pos.x,
                positionY: pos.y,
                widgetId: pos.id,
                setSize: getTaskSingleSize(pos),
              })),
            setKPIPositions: pos
              .filter((v) => v.kind === "WidgetKPI")
              .map((pos) => ({
                positionX: pos.c * 2 + pos.x,
                positionY: pos.y,
                widgetId: pos.id,
                setSize: getWidgetOverviewSize(pos),
              })),
            setListPositions: pos
              .filter((v) => v.kind === "WidgetList")
              .map((pos) => ({
                positionX: pos.c * 2 + pos.x,
                positionY: pos.y,
                widgetId: pos.id,
                setSize: getWidgetOverviewSize(pos),
              })),
            setTablePositions: pos
              .filter((v) => v.kind === "WidgetTable")
              .map((pos) => ({
                positionX: pos.c * 2 + pos.x,
                positionY: pos.y,
                widgetId: pos.id,
                setSize: getWidgetOverviewSize(pos),
              })),
            setNewsfeedPositions: pos
              .filter((v) => v.kind === "WidgetNewsfeed")
              .map((pos) => ({
                positionX: pos.c * 2 + pos.x,
                positionY: pos.y,
                widgetId: pos.id,
                setSize: getWidgetOverviewSize(pos),
              })),
          },
        },
      });
    },
    [mutate, storecastId]
  );

  return setPositions;
};
